import {
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Fragment } from "react";

import { routeProductsTestShackManager } from "@/setup/routes/products.routes";
import { MeasurementValue } from "@/pages/products/test-shack/shared/components/MeasurementValue";
import { TestShackReportInspectionMetaRow } from "@/pages/products/test-shack/shared/components/report/TestShackReportInspectionMetaRow";
import { TestShackReportStatusArrow as StatusArrow } from "@/pages/products/test-shack/shared/components/report/TestShackReportStatusArrow";
import { TEST_SHACK_REPORT_STATUS } from "@/pages/products/test-shack/models/TestShackReport";
import type {
	TestShackReportCell,
	TestShackReportSectionRev,
} from "../models/TestShackReportSectionRev";
import {
	formatMeasurement,
	isOutOfSpec,
} from "@/pages/products/test-shack/utils/reportFormat";
import { formatDeltaCode, showsNoSpecMarkers } from "../utils/format";
import { TestShackReportNoSpecDot } from "@/pages/products/test-shack/shared/components/report/TestShackReportNoSpec";
import {
	numericCell,
	SPEC_ROWS,
	stickyLabelCell,
} from "@/pages/products/test-shack/shared/components/report/testShackReportTable";

interface TestShackReportDimensionTableProps {
	sectionRev: TestShackReportSectionRev;
	showMinTargetMax: boolean;
}

const ReadingCell = ({
	cell,
	code,
	isEvaluated,
}: {
	cell: TestShackReportCell | null;
	code: string;
	/** False when the Section-Rev has no specification to judge against. */
	isEvaluated: boolean;
}) => {
	const status = cell?.status ?? TEST_SHACK_REPORT_STATUS.NO_SPEC;
	const hasIssue = isEvaluated && cell !== null && isOutOfSpec(status);
	const isMissing = isEvaluated && cell?.value == null;

	return (
		<TableCell
			data-test-id={`report-reading-${code}`}
			align="center"
			sx={{
				...numericCell,
				backgroundColor: hasIssue
					? (theme) => alpha(theme.palette.error.main, 0.08)
					: undefined,
				color: hasIssue || isMissing ? "error.main" : "text.primary",
				fontWeight: hasIssue ? 700 : 400,
				minWidth: 64,
			}}
		>
			<Stack
				direction="row"
				spacing={0.25}
				justifyContent="center"
				alignItems="center"
			>
				<MeasurementValue>
					{formatMeasurement(cell?.value ?? null)}
				</MeasurementValue>
				{hasIssue && <StatusArrow status={status} />}
			</Stack>
		</TableCell>
	);
};

export const TestShackReportDimensionTable = ({
	sectionRev,
	showMinTargetMax,
}: TestShackReportDimensionTableProps) => {
	const { columns, rows } = sectionRev;
	const showNoSpec = showsNoSpecMarkers(columns);

	if (columns.length === 0)
		return (
			<Typography color="text.secondary" sx={{ py: 2 }}>
				No persisted dimensions are available for this Section-Rev.
			</Typography>
		);

	return (
		<TableContainer
			component={Paper}
			variant="outlined"
			sx={{ flex: 1, minWidth: 0, overflowX: "auto" }}
		>
			<Table
				size="small"
				aria-label={`Inspections for ${sectionRev.sectionName}`}
				sx={{ "& .MuiTableCell-root": { px: 0.375, py: 0.5 } }}
			>
				<TableHead>
					<TableRow sx={{ backgroundColor: "action.hover" }}>
						<TableCell
							sx={{
								...stickyLabelCell,
								backgroundColor: "transparent",
								color: "text.secondary",
								fontSize: "11px",
								fontWeight: 600,
							}}
						>
							Dims.
						</TableCell>
						{columns.map((column) => (
							<TableCell
								key={column.key}
								data-test-id={`report-dimension-${column.code}`}
								align="center"
								sx={{
									color: "text.secondary",
									fontSize: "11px",
									fontWeight: 700,
									letterSpacing: "0.04em",
									minWidth: 64,
									textTransform: "uppercase",
								}}
							>
								<Tooltip title={column.name}>
									<span>
										{formatDeltaCode(column.code)}
										{showNoSpec && !column.hasSpec && (
											<TestShackReportNoSpecDot />
										)}
									</span>
								</Tooltip>
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{showMinTargetMax &&
						SPEC_ROWS.map((specRow) => (
							<TableRow key={specRow.id}>
								<TableCell
									component="th"
									scope="row"
									sx={{
										...stickyLabelCell,
										color: "text.secondary",
										fontSize: "12px",
									}}
								>
									{specRow.label}
								</TableCell>
								{columns.map((column) => (
									<TableCell
										key={column.key}
										align="center"
										sx={{
											...numericCell,
											color: "text.secondary",
											fontWeight: specRow.isEmphasized ? 700 : 400,
										}}
									>
										<MeasurementValue>
											{formatMeasurement(specRow.value(column))}
										</MeasurementValue>
									</TableCell>
								))}
							</TableRow>
						))}

					{rows.map((row) => (
						<Fragment key={row.inspection.ProfileInspectionId}>
							<TestShackReportInspectionMetaRow
								row={row}
								columnCount={columns.length + 1}
								managerRoute={routeProductsTestShackManager}
							/>
							<TableRow
								data-test-id={`report-readings-${row.inspection.ProfileInspectionId}`}
							>
								<TableCell
									component="th"
									scope="row"
									sx={{
										...stickyLabelCell,
										color: "text.secondary",
										fontSize: "12px",
									}}
								>
									Entered
								</TableCell>
								{row.cells.map((cell, index) => (
									<ReadingCell
										key={columns[index]?.key ?? String(index)}
										cell={cell}
										code={columns[index].code}
										isEvaluated={sectionRev.hasTolerances}
									/>
								))}
							</TableRow>
						</Fragment>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
